import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MagnifyingGlass, Plus, MapPin, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import api from '../lib/api';
import { useOwnerFetch } from '../lib/ownerFetch';
import GolfApiImport from '../components/GolfApiImport';
import { toast } from 'sonner';

export default function OwnerCoursesList() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const debounceRef = useRef(null);
  const { ownerFetch } = useOwnerFetch();
  const isOwner = !!sessionStorage.getItem('foretour_platform_token');

  useEffect(() => {
    api.get('/courses').then(r => setCourses(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(val), 150);
  };

  const filtered = useMemo(() => {
    if (!debounced.trim()) return courses;
    const q = debounced.trim().toLowerCase();
    return courses.filter(c => c.name.toLowerCase().includes(q) || (c.region || '').toLowerCase().includes(q));
  }, [courses, debounced]);

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="courses-list-page">
      <div className="sticky top-0 z-20 bg-[#1D242B]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white p-1" data-testid="courses-back-btn">
            <ArrowLeft className="w-5 h-5" weight="bold" />
          </button>
          <h1 className="text-lg text-white font-bold flex-1">Courses</h1>
          <Button size="sm" onClick={() => navigate('/admin/add-course')}
            className="h-8 px-3 text-xs bg-[#1B4332] text-white rounded-lg" data-testid="add-course-btn">
            <Plus className="w-4 h-4 mr-1" weight="bold" /> Add
          </Button>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B8C7C0]" />
            <Input placeholder="Search courses or clubs..." value={search}
              onChange={e => handleSearch(e.target.value)}
              className="pl-11 h-11 rounded-xl bg-[#F4F6F5] border-[#E1E6E4] text-sm"
              data-testid="courses-search" />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 max-w-lg mx-auto space-y-3">
        <GolfApiImport
          onImport={(course) => navigate('/admin/add-course', { state: { importedCourse: course } })}
          onLocalEdit={(oid) => navigate(`/admin/add-course?oid=${oid}`)}
        />
        {loading ? (
          <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <MagnifyingGlass className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
            <p className="text-[#455561] text-sm">No courses found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(c => (
              <Card key={c._id || c.id}
                className="bg-white border-[#E1E6E4] rounded-xl cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => navigate(`/admin/add-course?oid=${c._id}`)}
                data-testid={`course-row-${c._id || c.id}`}>
                <div className="flex items-center min-h-[56px] px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1D242B] text-sm truncate">{c.name}</p>
                    {c.region && <p className="text-xs text-[#455561] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{c.region}</p>}
                  </div>
                  <div className="text-right ml-3 flex-shrink-0 flex items-center gap-2">
                    <div>
                      <p className="text-xs text-[#455561]">Par {c.par}</p>
                      <p className="text-[10px] text-[#B8C7C0]">{(c.tees || []).length} tee{(c.tees || []).length !== 1 ? 's' : ''}</p>
                    </div>
                    {isOwner && c._id && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
                          const result = await ownerFetch(() => api.delete(`/owner/courses/${c._id}`));
                          if (result.ok) {
                            setCourses(prev => prev.filter(x => x._id !== c._id));
                            toast.success('Course deleted');
                          } else if (result.error) {
                            toast.error(result.error);
                          }
                        }}
                        className="p-1.5 rounded-md text-[#B8C7C0] hover:text-[#D32F2F] hover:bg-red-50 transition-colors"
                        data-testid={`course-delete-${c._id}`}>
                        <Trash className="w-4 h-4" weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
